import { LoginCredentialsOrTokenForm } from "@closerplatform/spinner-openapi";
import { FieldState, FormState } from "formstate";
import { observer } from "mobx-react";
import React, { useEffect, useState } from "react";
import { AgentInput } from "@fidget/common/components/form/agentInput";
import { L10n, L10nHtml } from "@fidget/common/i18n";
import { intl } from "@fidget/common/i18n/TypedIntl";
import { scrollToSection } from "@fidget/common/utils/scrollToSection";
import { unwrapFormState } from "@fidget/common/utils/unwrapFormState";
import { validateEmail, validateRequired } from "@fidget/common/utils/validatorsFormState";
import { OIDCState, useOIDCAuth } from "../../../hooks/useOIDCAuth";
import { useStore } from "../../../models";
import { ResetPassStatus } from "../../../models/currentUser";
import { BeforeLoginSection } from "../components/beforeLoginSection";
import { WelcomeToHeader } from "../components/welcomeToHeader";
import { LoginErrorSection } from "../loginOidcResponse/loginErrorSection";
import styles from "./login.css";
import { ToastArea } from "../../../components/toast/toastArea";

type LoginFields = {
  password: FieldState<string>;
  email: FieldState<string>;
};

enum LoginMode {
  MAGIC = "MAGIC",
  RESET = "RESET"
}

enum FocusedSection {
  Email = 0,
  Password = 1,
  ResetScreen = 2,
  MagicLink = 2,
  ResetRequestSent = 3
}

const createFormState = () => {
  return new FormState({
    password: new FieldState("").disableAutoValidation().validators(validateRequired),
    email: new FieldState("").disableAutoValidation().validators(validateRequired, validateEmail)
  });
};
type Form = ReturnType<typeof createFormState>;

/**
 * Componenent handling use cases related to login
 * TODO: refactor screenMode stuff to a simple state machine with current state in a single store observable
 */
export const Login = observer(() => {
  const { currentUserStore } = useStore();
  const [form] = useState<Form>(createFormState());
  const [screenMode, setScreenMode] = useState<LoginMode>();

  const { oidcLoadingState } = useOIDCAuth();

  useEffect(() => {
    currentUserStore.clearErrors();
  }, []);

  useEffect(() => {
    if (currentUserStore.loginError) {
      form.$.password.setError(currentUserStore.loginError);
    }
  }, [currentUserStore.loginError]);

  const handleEmailSubmit = async (ev: React.FormEvent<HTMLFormElement>) => {
    ev.preventDefault();
    await form.$.email.validate();
    if (!form.$.email.hasError) {
      scrollToSection(FocusedSection.Password);
    }
  };

  const handleBackToEmail = (ev: React.SyntheticEvent<HTMLAnchorElement>) => {
    ev.preventDefault();
    setScreenMode(undefined);
    currentUserStore.setResetPassStatus(ResetPassStatus.NONE);
    scrollToSection(FocusedSection.Email, true);
  };

  const login = async (ev: React.FormEvent<HTMLFormElement>) => {
    ev.preventDefault();
    const result = await form.validate();
    if (!result.hasError) {
      const credentials = unwrapFormState<LoginFields>(result.value);
      currentUserStore.login(credentials as unknown as LoginCredentialsOrTokenForm);
    }
  };

  const handleMagicLink = async (ev: React.SyntheticEvent<HTMLButtonElement>) => {
    const result = await form.$.email.validate();
    if (!result.hasError) {
      currentUserStore.requestMagicLinkLogin(result.value);
      setScreenMode(LoginMode.MAGIC);
      scrollToSection(FocusedSection.MagicLink);
    }
  };

  const handleForgotPass = () => {
    setScreenMode(LoginMode.RESET);
    setTimeout(scrollToSection, 0, FocusedSection.ResetScreen);
  };

  const handleRequestResetLink = async (
    ev: React.FormEvent<HTMLFormElement> | React.SyntheticEvent<HTMLButtonElement>
  ) => {
    ev.preventDefault();
    const result = await form.$.email.validate();
    if (!result.hasError) {
      await currentUserStore.requestPasswordResetLink(result.value);
      scrollToSection(FocusedSection.ResetRequestSent);
    }
  };

  const renderLogin = () => {
    switch (screenMode) {
      case LoginMode.MAGIC:
        return renderMagicInfo();
      case LoginMode.RESET:
        return renderResetPass();
      default:
        return undefined;
    }
  };

  const renderFooter = () => (
    <div className={styles.footer}>
      <p>
        <L10n id={"login.notYourEmail"} />
      </p>
      <p>
        <a href={"#"} onClick={handleBackToEmail}>
          <L10n id={"login.backToSignIn"} />
        </a>
      </p>
    </div>
  );

  const renderEmailPassword = (isAfterReset?: boolean) => {
    return (
      <>
        <ToastArea />
        <BeforeLoginSection styleType="login">
          <header>{isAfterReset && <h2>Password has been successfully reset</h2>}</header>
          <form onSubmit={handleEmailSubmit}>
            <AgentInput
              field={form.$.email}
              styleType="login"
              placeholder={intl.formatMessage("login.password.placeholder.email")}
              maxLength={64}
              autoComplete={"email"}
              autoFocus
            />
            <button type={"submit"}>
              <L10n id={"login.password.button.next"} />
            </button>
          </form>
        </BeforeLoginSection>
        <BeforeLoginSection styleType="login">
          <header>
            <h2>{form.$.email.$}</h2>
          </header>
          <form onSubmit={login}>
            <AgentInput
              field={form.$.password}
              styleType="login"
              type={"password"}
              placeholder={intl.formatMessage("login.password.placeholder.password")}
              autoComplete={"password"}
              autoFocus
            />
            <button type={"submit"} id={"sign_in_button"}>
              <L10n id={"login.password.button.signin"} />
            </button>
            <p id={"forgot_password_link"}>
              <a href={"#"} onClick={handleForgotPass}>
                <L10n id={"login.password.forgotPassword"} />
              </a>
            </p>
          </form>
          <div>
            <p>
              <L10n id={"login.password.or"} />
            </p>
            <button type={"button"} onClick={handleMagicLink} id={"magic_link_button"}>
              <L10n id={"login.password.button.sendMagicLink"} />
            </button>
            <p>
              <L10n id={"login.password.magicLinkDisclaimer"} />
            </p>
          </div>
          {renderFooter()}
        </BeforeLoginSection>
      </>
    );
  };

  const renderMagicInfo = () => {
    return (
      <BeforeLoginSection styleType="login">
        <header>
          <h2>
            <L10n id={"checkYourEmail"} />
          </h2>
        </header>
        <form>
          <p>
            <L10nHtml id={"login.magicLink.emailSent"} values={{ email: form.$.email.$ }} />
          </p>
        </form>
        <div>
          <p>
            <L10n id={"login.resend.title"} />
          </p>
          <button type={"button"} onClick={handleMagicLink}>
            <L10n id={"login.resend.button"} />
          </button>
        </div>
        {renderFooter()}
      </BeforeLoginSection>
    );
  };

  const renderResetPass = () => {
    return (
      <>
        <BeforeLoginSection styleType="login">
          <form onSubmit={handleRequestResetLink}>
            <header>
              <h2>
                <L10nHtml id={"login.resetPassword.resetRequestLabel"} />
              </h2>
            </header>
            <AgentInput
              field={form.$.email}
              styleType="login"
              placeholder={intl.formatMessage("login.password.placeholder.email")}
              autoComplete={"email"}
              autoFocus
            />
            <button type={"submit"}>
              <L10n id={"login.password.button.submit"} />
            </button>
            <p>
              <L10nHtml id={"login.resetPassword.resetPassInfo"} />
            </p>
          </form>
          <div className={styles.footer}>
            <a href={"#"} onClick={handleBackToEmail}>
              <L10n id={"login.backToSignIn"} />
            </a>
          </div>
        </BeforeLoginSection>
        <BeforeLoginSection styleType="login">
          <header>
            <h2>
              <L10n id={"login.resetPassword.resetRequestLabel"} />
            </h2>
          </header>
          <form>
            <p>
              <L10nHtml id={"login.resetPassword.emailSent"} values={{ email: form.$.email.$ }} />
            </p>
          </form>
          <div>
            <p>
              <L10n id={"login.resend.title"} />
            </p>
            <button type={"button"} onClick={handleRequestResetLink}>
              <L10n id={"login.resend.button"} />
            </button>
          </div>
          {renderFooter()}
        </BeforeLoginSection>
      </>
    );
  };

  const render = () => {
    switch (oidcLoadingState) {
      case OIDCState.Failed:
        return <LoginErrorSection />;
      case OIDCState.Pristine:
        return (
          <>
            {renderEmailPassword(currentUserStore.resetPassStatus === ResetPassStatus.FINISHED)}
            {renderLogin()}
          </>
        );
      default:
        return;
    }
  };

  return (
    <div className={styles["login-background"]}>
      <WelcomeToHeader styleType="login">{render()}</WelcomeToHeader>
    </div>
  );
});
